<template>
  <BaseLayout title="Global File">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Global File</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="globalFiles.length === 0" class="alert alert-info text-center">
          No global files available.
          <button @click="fetchAgainGlobalFiles" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i>Refresh
          </button>
        </div>
        <FilesList
          v-else
          title="Global Files"
          :files="globalFiles.map(file => file.file_path)"
          :filesMetadata="globalFiles.map(file => ({
            file_path: file.file_metadata.file_path,
            size: file.file_metadata.size,
            modified_time: file.file_metadata.modified_time,
          }))"
          :onViewFile="showFileContent"
          :onDownloadFile="downloadFile"
          :onDeleteFile="deleteGlobalFile"
        />
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <button type="button" class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#submitFilesModal">
          <i class="bi bi-upload me-2"></i>Upload Global File
        </button>
      </div>
    </template>
    <SubmitFilesModal
      ref="submitFilesModal"
      :multiple="false"
      @submitForm="uploadGlobalFile"
    />
    
    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import FilesList from '@/components/FilesList.vue';
import SubmitFilesModal from '@/components/SubmitFilesModal.vue';
import GlobalFileController from '@/controllers/GlobalFileController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'GlobalFileView',
  components: {
    BaseLayout,
    FilesList,
    SubmitFilesModal,
    StemErrorModal
  },
  data() {
    return {
      globalFiles: [],
      loading: true,
    };
  },
  computed: {
    ...mapGetters(['isTeacher']),
    formatGobalFileDate() {
      return (date) => GlobalFileController.formatDate(date);
    },
  },
  async created() {
    try {
      // Check if the user is a teacher
      if (!this.isTeacher) {
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: 'You are not authorized to access this page.',
        });
        this.$router.push('/');
        return;
      }
      this.globalFiles = await GlobalFileController.fetchGlobalFiles();
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    } 
  },
  methods: {
    async uploadGlobalFile(globalFile) {
      try {
        if (globalFile.length == 1) {
          const formData = new FormData();
          formData.append('file', globalFile[0]);

          await GlobalFileController.uploadGlobalFile(formData);
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Global file uploaded successfully',
          });

          // Fetch again global files to update the list
          this.fetchAgainGlobalFiles();          
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteGlobalFile(globalFilePath) {
      if (!window.confirm('Are you sure you want to delete this file?')) {
        return;
      }
      try {
        // get global file id by matching file path, because component FilesListForm returns only file path
        const globalFileId = this.globalFiles.find((globalFile) => globalFile.file_path === globalFilePath).id;
        await GlobalFileController.deleteGlobalFile(globalFileId);
        this.globalFiles = this.globalFiles.filter((globalFile) => globalFile.id !== globalFileId);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Success',
          message: 'Global file deleted successfully',
        });

        // Fetch again global files to update the list
        this.fetchAgainGlobalFiles();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async fetchAgainGlobalFiles() {
      this.loading = true;
      try {
        this.globalFiles = await GlobalFileController.fetchGlobalFiles();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async showFileContent(filePath) {
      try {
        const response = await GlobalFileController.downloadGlobalFile(filePath);
        // open file in new tab
        window.open(URL.createObjectURL(new Blob([response.data])));
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await GlobalFileController.downloadGlobalFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    getFileName(filePath) {
      // Handle both forward and backward slashes
      return filePath.split('/').pop();
    },
  },
};
</script>